/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Pilcore PMS – Apply Market Price Reference Popup
 * Autor / Author: Alberto Castro Ull
 */

import React, { useState, useMemo, useEffect } from "react";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "./PmsThemeContext";
import "../EditRatePrice.css";

const ApplyScrapedPricesPopup = ({
  isOpen = true,
  sources = [],
  targets = [],
  businessUnitId,
  onClose,
  onApply,
  hideSeriesSelector = false,
  initialSelectedKeys = [],
}) => {
  const { themeId } = usePmsTheme();
  const isLightTheme = themeId === "light";

  const [selectedRateId, setSelectedRateId] = useState("");
  const [roomRates, setRoomRates] = useState([]);

  const independentRoomRates = useMemo(() => {
    const arr = Array.isArray(roomRates) ? roomRates : [];
    return arr.filter((r) => r?.base_rate_id == null);
  }, [roomRates]);


  const [mode, setMode] = useState("range");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [individualDates, setIndividualDates] = useState([]);

  const [selectedSourceKeys, setSelectedSourceKeys] = useState([]);
  const [combineMethod, setCombineMethod] = useState("average");
  const [overwrite, setOverwrite] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (Array.isArray(initialSelectedKeys)) {
      setSelectedSourceKeys([...initialSelectedKeys]);
    }
  }, [isOpen, initialSelectedKeys]);


  useEffect(() => {
    if (!businessUnitId) return;
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/roomRates/all`, {
        params: { business_unit_id: businessUnitId }
      })
      .then(res => setRoomRates(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error("❌ [ApplyPopup] Error loading room rates:", err?.response?.data || err);
        setRoomRates([]);
      });
  }, [businessUnitId]);

    const effectiveSources = useMemo(() => {
    if (Array.isArray(sources) && sources.length) return sources;
    return (targets || []).map((t) => ({
      key: `scraper:target:${t.id}`,
      kind: "scraper",
      label: t?.params?.name || t?.params?.destination || `Target ${t.id}`,
      targetId: t.id,
      pricesByDate: t.pricesByDate || {},
    }));
  }, [sources, targets]);

  // ✅ Show EXACTLY what you see on screen:
  // For ratemetrics: merge same (hotel_code, website_code, occupancy, los, room_type)
  // and per date keep MIN price.
  const displaySources = useMemo(() => {
    const out = [];
    const map = new Map(); // mergeKey -> mergedSource

    for (const s of effectiveSources || []) {
      if (String(s?.kind || "") !== "ratemetrics") {
        out.push(s);
        continue;
      }

            const hotel_code = s?.hotel_code ?? null;
      const hotel_name = s?.hotel_name ?? null;

      // ✅ Keep these defined because they are used later (label/items), even if mergeKey won't include them
      const website_code = s?.website_code ?? null;
      const occupancy = s?.occupancy ?? null;
      const los = s?.los ?? null;

      // ✅ Normalize room type like the table (avoid duplicates by formatting/case)
      const roomTypeDisplay = String(s?.room_type || "—").trim() || "—";
      const room_type = roomTypeDisplay; // keep the same variable name used below
      const roomTypeKey = roomTypeDisplay.toLowerCase();

      const hotelCodeKey =
        hotel_code === null || hotel_code === undefined || String(hotel_code).trim() === ""
          ? "x"
          : String(Number(hotel_code));

      // ✅ Merge EXACTLY by hotel + room_type (like the visible table grouping)
      const mergeKey = `rm:${hotelCodeKey}:${roomTypeKey}`;


      let m = map.get(mergeKey);
      if (!m) {
        m = {
          key: mergeKey,
          kind: "ratemetrics",
          label: `${hotel_name || (hotel_code ? `Hotel ${hotel_code}` : "Hotel")} — ${room_type || "—"}`,
          hotel_code,
          hotel_name,
          room_type,
          website_code,
          occupancy,
          los,
          pricesByDate: {},
          // ✅ keep ALL variants so backend can re-query if needed
          items: [],
        };
        map.set(mergeKey, m);
      }

      // merge pricesByDate => MIN per date
      const pbd = s?.pricesByDate || {};
      for (const [d, v] of Object.entries(pbd)) {
        const dd = String(d).slice(0, 10);
        if (dd.length !== 10) continue;

        const num = v == null ? null : Number(v);
        if (!Number.isFinite(num)) continue;

        const prev = m.pricesByDate[dd];
        if (prev == null || num < prev) m.pricesByDate[dd] = Number(num.toFixed(2));
      }

      // store original “selector” fields (rate_plan / meal_plan variants)
      m.items.push({
        hotel_code: s.hotel_code ?? null,
        hotel_name: s.hotel_name ?? null,
        room_type: s.room_type ?? null,
        website_code: s.website_code ?? null,
        occupancy: s.occupancy ?? null,
        los: s.los ?? null,
        // if your source objects already carry these, they’ll pass through:
        meal_plan: s.meal_plan ?? null,
        rate_plan: s.rate_plan ?? null,
        rate_plan_code: s.rate_plan_code ?? null,
      });
    }

    const merged = Array.from(map.values());
    merged.sort((a, b) => String(a.label).localeCompare(String(b.label)));

    return [...merged, ...out].sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [effectiveSources]);

    const availableDates = useMemo(() => {
    const dateSet = new Set();
    for (const s of displaySources) {
      const pbd = s?.pricesByDate || {};
      if (pbd && typeof pbd === "object") {
        Object.keys(pbd).forEach((d) => {
          const dd = String(d).slice(0, 10);
          if (dd.length === 10) dateSet.add(dd);
        });
      }
    }
    return Array.from(dateSet).sort();
  }, [displaySources]);


  // ✅ ahora SÍ: cortar render después de hooks
  if (!isOpen) return null;

  const handleToggleKey = (key) => {
    setSelectedSourceKeys((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const handleAddDate = (e) => {
    const raw = String(e.target.value || "").slice(0, 10);
    if (raw.length !== 10) return;
    if (!individualDates.includes(raw)) setIndividualDates([...individualDates, raw]);
  };

  const handleRemoveDate = (index) => {
    const updated = [...individualDates];
    updated.splice(index, 1);
    setIndividualDates(updated);
  };

    const handleSubmit = async () => {
    // 1) destino
    if (!selectedRateId) {
      alert("Please select a room rate");
      return;
    }

    // 2) fuentes elegidas
    const chosen = displaySources.filter((s) => selectedSourceKeys.includes(s.key));
    if (chosen.length === 0) {
      alert("Please choose at least one source.");
      return;
    }

    // 3) No mezclar providers en una misma aplicación
    const kinds = Array.from(new Set(chosen.map((s) => String(s.kind || ""))));
    if (kinds.length > 1) {
      alert("Please select sources from a single provider.");
      return;
    }

    // 4) fechas
    let dates = null;
    if (mode === "range") {
      if (!rangeStart || !rangeEnd) {
        alert("Please select a start and end date.");
        return;
      }
      dates = { start: rangeStart, end: rangeEnd };
    } else {
      if (!individualDates.length) {
        alert("Please select at least one date.");
        return;
      }
      dates = individualDates;
    }

    const payload = {
      room_rate_id: Number(selectedRateId),
      dates,
      overwrite,
    };

    const kind = kinds[0];

    const redirectToRate = () => {
      try {
        localStorage.setItem("palantir_lastRateTab", "market");
      } catch (e) {}
      const bu = businessUnitId != null ? Number(businessUnitId) : null;
      const qs = bu ? `?business_unit_id=${bu}` : "";
      window.location.href = `/edit-room-rate/${Number(selectedRateId)}${qs}`;
    };

    // ✅ ratemetrics
    if (kind === "ratemetrics") {
      payload.source = "ratemetrics";
      payload.combine = combineMethod;

      payload.items = chosen.flatMap((s) => {
        if (Array.isArray(s.items) && s.items.length) return s.items;
        return [{
          hotel_code: s.hotel_code ?? null,
          hotel_name: s.hotel_name ?? null,
          room_type: s.room_type ?? null,
          website_code: s.website_code ?? null,
          occupancy: s.occupancy ?? null,
          los: s.los ?? null,
          meal_plan: s.meal_plan ?? null,
          rate_plan_code: s.rate_plan_code ?? null,
        }];
      });

      console.log("🚀 [ApplyPopup] payload (ratemetrics):", payload);

      try {
        await Promise.resolve(onApply(payload));
        redirectToRate();
      } catch (err) {
        console.error("❌ [ApplyPopup] onApply failed:", err?.response?.data || err);
        alert("Failed to apply prices.");
      }
      return;
    }

    // xotelo
    if (kind === "xotelo") {
      payload.source = "xotelo";
      payload.combine = chosen.length > 1 ? combineMethod : null;
      payload.ota_names = Array.from(new Set(chosen.map((s) => s.ota_name).filter(Boolean)));
      payload.scraper_target_ids = Array.from(new Set(chosen.map((s) => s.targetId).filter(v => v != null))).map(Number);
      console.log("🚀 [ApplyPopup] payload (xotelo):", payload);

      try {
        await Promise.resolve(onApply(payload));
        redirectToRate();
      } catch (err) {
        console.error("❌ [ApplyPopup] onApply failed:", err?.response?.data || err);
        alert("Failed to apply prices.");
      }
      return;
    }

    // scraper
    payload.source = "scraper";
    payload.combine = chosen.length > 1 ? combineMethod : null;
    payload.target_ids = Array.from(new Set(chosen.map((s) => s.targetId).filter(v => v != null))).map(Number);
    console.log("🚀 [ApplyPopup] payload (scraper):", payload);

    try {
      await Promise.resolve(onApply(payload));
      redirectToRate();
    } catch (err) {
      console.error("❌ [ApplyPopup] onApply failed:", err?.response?.data || err);
      alert("Failed to apply prices.");
    }
  };


  return (
    <div
      className="erp-marketApplyPopup"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(7,15,36,0.78)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        "--erp-modal-bg": isLightTheme ? "#ffffff" : "#0e1730",
        "--erp-modal-section": isLightTheme ? "#f7fafc" : "#121f3d",
        "--erp-modal-input": isLightTheme ? "#ffffff" : "#152344",
        "--erp-modal-title": isLightTheme ? "#172653" : "#f2f6ff",
        "--erp-modal-text": isLightTheme ? "#26354f" : "#dfe8f7",
        "--erp-modal-muted": isLightTheme ? "#5b6680" : "#aebbd0",
        "--erp-modal-border": isLightTheme ? "#d7e2ec" : "rgba(92,134,181,0.34)",
        "--erp-modal-border-strong": isLightTheme ? "#7c90a7" : "rgba(61,182,233,0.58)",
        "--erp-modal-shadow": isLightTheme ? "0 18px 44px rgba(34,58,122,0.16)" : "0 24px 54px rgba(2,8,28,0.52)",
      }}
      onClick={onClose}
    >
      <div
        className="erp-marketApplyShell"
        style={{
          width: "min(900px, 92vw)",
          maxHeight: "85vh",
          padding: "18px",
          borderRadius: "16px",
          border: "1px solid var(--erp-modal-border)",
          borderTop: "3px solid #3db6e9",
          background: "var(--erp-modal-bg)",
          color: "var(--erp-modal-text)",
          boxShadow: "var(--erp-modal-shadow)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          fontSize: "0.90rem",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h6 className="erp-marketApplyTitle mb-2" style={{ margin: 0 }}>
          Apply Market Price Reference
        </h6>
        <div className="erp-marketApplySub mb-3" style={{ fontSize: "0.78rem" }}>
          Select destination rate + competitor series to write into Market Prices.
        </div>

        <div style={{ overflowY: "auto", paddingRight: 6 }}>
          {/* Destino */}
          <div className="mb-3">
            <label className="form-label">Room Rate (destination)</label>
            <select
              className="form-select form-select-sm"
              value={selectedRateId}
              onChange={(e) => setSelectedRateId(e.target.value)}
            >
              <option value="">Select room rate…</option>
                            {independentRoomRates.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.rate_code}) — {r?.room_category?.name || "No category"}
                </option>
              ))}


            </select>
          </div>

          {/* Fechas */}
          <div className="mb-2">
            <label className="form-label">Date selection mode</label>
            <select
              className="form-select form-select-sm"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              <option value="range">Range</option>
              <option value="individual">Individual dates</option>
            </select>
          </div>

          {mode === "range" && (
            <div className="mb-3 d-flex gap-2">
              <div className="flex-fill">
                <label className="form-label">Start</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  min={availableDates[0] || undefined}
                  max={availableDates[availableDates.length - 1] || undefined}
                />
              </div>
              <div className="flex-fill">
                <label className="form-label">End</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  min={availableDates[0] || undefined}
                  max={availableDates[availableDates.length - 1] || undefined}
                />
              </div>
            </div>
          )}

          {mode === "individual" && (
            <div className="mb-3">
              <label className="form-label">Select dates</label>
              <input
                type="date"
                className="form-control form-control-sm"
                onChange={handleAddDate}
                min={availableDates[0] || undefined}
                max={availableDates[availableDates.length - 1] || undefined}
              />
              <div className="d-flex flex-wrap gap-2 mt-2">
                {individualDates.map((d, index) => (
                  <span
                    key={d}
                    className="erp-marketApplyDate badge d-flex align-items-center"
                    style={{ padding: "0.35rem 0.55rem", borderRadius: "1rem" }}
                  >
                    {d}
                    <button
                      className="btn btn-sm btn-close ms-2"
                      onClick={() => handleRemoveDate(index)}
                      style={{ fontSize: "0.6rem" }}
                    />
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Sources */}
          {!hideSeriesSelector && (
          <div className="mb-3">
            <label className="form-label">Competitor series</label>
            <div
              style={{
                maxHeight: "34vh",
                overflowY: "auto",
                border: "1px solid var(--erp-modal-border)",
                borderRadius: 8,
                padding: "10px",
                background: "var(--erp-modal-section)",
              }}
            >
              {displaySources.length === 0 && (
                <div className="text-muted">No sources available.</div>
              )}

              {displaySources.map((s) => (
                <div key={s.key} className="form-check" style={{ marginBottom: 6 }}>

                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`col-${s.key}`}
                    checked={selectedSourceKeys.includes(s.key)}
                    onChange={() => handleToggleKey(s.key)}
                  />
                  <label className="form-check-label" htmlFor={`col-${s.key}`}>
                    {s.label}
                    {s.kind === "xotelo" ? " (TripAdvisor)" : s.kind === "scraper" ? " (Scraper)" : ""}
                  </label>

                </div>
              ))}
            </div>
          </div>
          )}

          <select
            className="form-select form-select-sm"
            value={combineMethod}
            onChange={(e) => setCombineMethod(e.target.value)}
          >
            <option value="average">Average</option>
            <option value="min">Minimum</option>
            <option value="max">Maximum</option>
          </select>

          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="overwrite"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="overwrite">
              Overwrite existing prices
            </label>
          </div>
        </div>

        <div className="d-flex justify-content-between mt-3">
          <button onClick={onClose} className="erp-marketApplyCancel btn btn-sm">
            Cancel
          </button>
          <button onClick={handleSubmit} className="erp-marketApplyConfirm btn btn-sm">
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplyScrapedPricesPopup;
